import { Button, Card, Col, Form, Input, Modal, Popconfirm, Row, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import PermissionService from '../../services/PermissonService';
import { IPermissionResponseData, PermissionAction } from '../../types/Permission';
import { openNotification } from '../../utils';
import PenIcon from '../../assets/icons/pen.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import './../../App.scss';

const { Title } = Typography;

type PermissionFormValues = {
  name: string;
  description?: string;
  action?: PermissionAction;
  entityName?: string;
};

const normalizeSearch = (value: string) => value.toLocaleLowerCase('tr-TR').trim();
const { TextArea } = Input;

const actionOptions: Array<{ label: string; value: PermissionAction }> = [
  { label: 'Oluşturma', value: 'CREATE' },
  { label: 'Görüntüleme', value: 'READ' },
  { label: 'Güncelleme', value: 'UPDATE' },
  { label: 'Silme', value: 'DELETE' },
];

const getEntityName = (permission: IPermissionResponseData) => permission.entityName ?? permission.entity_name ?? '';
const displayValue = (value?: string) => value?.trim() || '-';
const trimOptional = (value?: string) => {
  const trimmedValue = value?.trim();
  return trimmedValue || undefined;
};

const PermissionList = () => {
  const [permissions, setPermissions] = useState<IPermissionResponseData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPermission, setEditingPermission] = useState<IPermissionResponseData | null>(null);
  const [form] = Form.useForm<PermissionFormValues>();

  const filteredPermissions = useMemo(() => {
    const normalizedSearchValue = normalizeSearch(searchTerm);

    if (!normalizedSearchValue) {
      return permissions;
    }

    return permissions.filter((permission) => {
      const searchableFields = [
        permission.name,
        permission.description,
        permission.action,
        getEntityName(permission),
      ].filter((field): field is string => typeof field === 'string');

      return searchableFields.some((field) => field.toLocaleLowerCase('tr-TR').includes(normalizedSearchValue));
    });
  }, [permissions, searchTerm]);

  const getPermissions = () => {
    setLoading(true);
    PermissionService.getAll()
      .then((response: any) => {
        const sortedData = [...response.data].sort((first, second) => first.name.localeCompare(second.name, 'tr-TR'));
        setPermissions(sortedData);
      })
      .catch((error: Error) => {
        console.error('Yetkiler alınamadı', error);
        openNotification('error', 'Hata', 'Yetkiler alınamadı.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getPermissions();
  }, []);

  const openCreateModal = () => {
    setEditingPermission(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const openEditModal = (permission: IPermissionResponseData) => {
    setEditingPermission(permission);
    form.setFieldsValue({
      name: permission.name,
      description: permission.description,
      action: permission.action,
      entityName: getEntityName(permission),
    });
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setEditingPermission(null);
    form.resetFields();
  };

  const onFinish = (values: PermissionFormValues) => {
    const payload = {
      name: values.name.trim(),
      description: trimOptional(values.description),
      action: values.action,
      entityName: trimOptional(values.entityName),
    };

    const request = editingPermission?.id
      ? PermissionService.update(editingPermission.id, payload)
      : PermissionService.create(payload);

    request
      .then(() => {
        openNotification('success', 'Başarılı', editingPermission ? 'Yetki başarıyla güncellendi.' : 'Yetki başarıyla eklendi.');
        closeModal();
        getPermissions();
      })
      .catch((error: Error) => {
        console.error('Yetki kaydedilemedi', error);
        openNotification('error', 'Hata', 'Yetki kaydedilemedi.');
      });
  };

  const deletePermission = (id: number) => {
    PermissionService.remove(id)
      .then(() => {
        openNotification('success', 'Başarılı', 'Yetki başarıyla silindi.');
        getPermissions();
      })
      .catch((error: Error) => {
        console.error('Yetki silinemedi', error);
        openNotification('error', 'Hata', 'Yetki silinemedi.');
      });
  };

  const columns: ColumnsType<IPermissionResponseData> = [
    {
      title: 'Yetki Kodu',
      dataIndex: 'name',
      key: 'name',
      sorter: (first, second) => first.name.localeCompare(second.name, 'tr-TR'),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (value?: string) => displayValue(value),
    },
    {
      title: 'İşlem',
      dataIndex: 'action',
      key: 'action',
      width: 140,
      render: (value?: PermissionAction) => actionOptions.find((option) => option.value === value)?.label ?? displayValue(value),
      sorter: (first, second) => (first.action ?? '').localeCompare(second.action ?? '', 'tr-TR'),
    },
    {
      title: 'Varlık Adı',
      key: 'entityName',
      width: 180,
      render: (_, row) => displayValue(getEntityName(row)),
      sorter: (first, second) => getEntityName(first).localeCompare(getEntityName(second), 'tr-TR'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 140,
      render: (_, row) => (
        <Space size={14}>
          <Button
            icon={<img src={PenIcon} alt="Düzenle" className="icon" />}
            shape="circle"
            onClick={() => openEditModal(row)}
            className="icon-button"
          />
          <Popconfirm
            placement="left"
            title="Yetkiyi silmek istediğinize emin misiniz?"
            onConfirm={() => deletePermission(row.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              icon={<img src={TrashIcon} alt="Sil" className="icon" />}
              shape="circle"
              className="icon-button"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="permission-container">
      <Card className="br-30px">
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Title style={{ margin: 0, display: 'inline-block' }} level={4}>Yetki Listesi</Title>
            <Typography.Text type="secondary" className="ms-5">
              <strong>({filteredPermissions.length})</strong> kayıt listelendi
            </Typography.Text>
          </Col>
          <Col>
            <Row>
              <Input
                allowClear
                placeholder="Ara"
                prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                className="d-input d-input-form mr-16px d-input-filter"
                style={{ width: 220, marginRight: 16 }}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <Button type="primary" onClick={openCreateModal} className="width-auto d-button dark-button">
                Yeni Yetki Ekle
              </Button>
            </Row>
          </Col>
        </Row>

        <Row className="mt-30px">
          <Col span={24}>
            <Table
              scroll={{ x: 980 }}
              loading={loading}
              columns={columns}
              dataSource={filteredPermissions}
              rowKey={(record) => record.id}
              pagination={false}
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title={editingPermission ? 'Yetki Düzenle' : 'Yeni Yetki Ekle'}
        visible={isModalVisible}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        width={640}
      >
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item
            name="name"
            label="Yetki Kodu"
            rules={[
              { required: true, message: 'Lütfen yetki kodunu giriniz.' },
              { pattern: /^[a-zA-Z0-9_.:-]+$/, message: 'Yetki kodu yalnızca harf, rakam, nokta, alt çizgi, iki nokta veya kısa çizgi içerebilir.' },
            ]}
          >
            <Input placeholder="Örn: users.show" className="d-input d-input-form" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Açıklama"
          >
            <TextArea rows={3} placeholder="Yetkinin kullanım amacını giriniz" className="d-input d-input-form" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="action"
                label="İşlem"
              >
                <Select
                  allowClear
                  placeholder="İşlem seçiniz"
                  options={actionOptions}
                  className="d-input-form"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="entityName"
                label="Varlık Adı"
              >
                <Input placeholder="Örn: users" className="d-input d-input-form" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Row justify="center">
              <Button type="primary" htmlType="submit" className="d-button width-auto dark-button">
                Kaydet
              </Button>
            </Row>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PermissionList;
